import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {AppOrgTabs} from './AppOrgTabs';
import styles from './Apps.module.css';
import {AppShelf} from './AppShelf';
import {CatalogOrgSection, getOrgAccents, getOrgSectionLabel} from './catalogOrgs';
import {useShelfBleed} from './useShelfBleed';

// How long scroll-position tracking stays paused after the last scroll event of a tab jump.
const JUMP_SETTLE_MS = 150;
const SECTION_GAP_PX = 12;

const getScrollOffset = (tabs: HTMLElement) =>
  (parseFloat(getComputedStyle(tabs).top) || 0) + tabs.offsetHeight + SECTION_GAP_PX;

const scrollWindowTo = (top: number) => {
  if (typeof window.scrollTo === 'function') {
    window.scrollTo({top, behavior: 'smooth'});
  }
};

/**
 * Org shelves under a sticky tab row. Tabs jump to their shelf, and the active tab follows
 * the shelf at the top of the viewport as the page scrolls. Empty sections (no search matches)
 * hide their shelf and disable their tab, so the row doesn't shift while typing.
 */
export const AppOrgShelves = ({sections}: {sections: CatalogOrgSection[]}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
  const jumpTimerRef = useRef<number | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);

  const tabs = useMemo(
    () =>
      sections.map((section) => ({
        disabled: section.apps.length === 0,
        id: section.id,
        label: getOrgSectionLabel(section),
      })),
    [sections]
  );
  const visibleSections = useMemo(
    () => sections.filter((section) => section.apps.length > 0),
    [sections]
  );
  const accents = useMemo(() => getOrgAccents(sections), [sections]);

  const syncActiveTab = useCallback(() => {
    const tabsElement = tabsRef.current;
    if (!tabsElement || jumpTimerRef.current !== null) return;
    const offset = getScrollOffset(tabsElement);
    const scrolledToBottom =
      window.scrollY > 0 &&
      window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;

    let nextId: string | null = null;
    for (const section of visibleSections) {
      const element = sectionRefs.current[section.id];
      if (element && element.getBoundingClientRect().top <= offset + 1) nextId = section.id;
    }
    // Short trailing shelves can never reach the top, so the page bottom selects the last one.
    if (scrolledToBottom && nextId !== null) {
      nextId = visibleSections[visibleSections.length - 1].id;
    }
    setActiveId(nextId);
  }, [visibleSections]);

  useEffect(() => {
    const handleScroll = () => {
      if (jumpTimerRef.current !== null) {
        // Hold the clicked tab until the smooth scroll settles.
        window.clearTimeout(jumpTimerRef.current);
        jumpTimerRef.current = window.setTimeout(() => {
          jumpTimerRef.current = null;
        }, JUMP_SETTLE_MS);
        return;
      }
      syncActiveTab();
    };

    syncActiveTab();
    window.addEventListener('scroll', handleScroll, {passive: true});
    window.addEventListener('resize', syncActiveTab);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', syncActiveTab);
    };
  }, [syncActiveTab]);

  useShelfBleed(rootRef);

  useEffect(
    () => () => {
      if (jumpTimerRef.current !== null) window.clearTimeout(jumpTimerRef.current);
    },
    []
  );

  const jumpTo = (sectionId: string | null) => {
    const tabsElement = tabsRef.current;
    const target = sectionId ? sectionRefs.current[sectionId] : rootRef.current;
    if (!tabsElement || !target) return;

    setActiveId(sectionId);
    if (jumpTimerRef.current !== null) window.clearTimeout(jumpTimerRef.current);
    jumpTimerRef.current = window.setTimeout(() => {
      jumpTimerRef.current = null;
    }, JUMP_SETTLE_MS);

    const offset = sectionId
      ? getScrollOffset(tabsElement)
      : parseFloat(getComputedStyle(tabsElement).top) || 0;
    scrollWindowTo(target.getBoundingClientRect().top + window.scrollY - offset);
  };

  return (
    <div ref={rootRef}>
      <AppOrgTabs ref={tabsRef} tabs={tabs} activeId={activeId} onSelect={jumpTo} />
      <div className={styles.appOrgShelves}>
        {visibleSections.map((section) => (
          <AppShelf
            key={section.id}
            ref={(element) => {
              sectionRefs.current[section.id] = element;
            }}
            accent={accents.get(section.id)}
            apps={section.apps}
            heading={getOrgSectionLabel(section)}
            withActions={section.withActions}
          />
        ))}
      </div>
    </div>
  );
};
